import React, { useState, useEffect } from 'react';
import { 
  Award, Users, CheckCircle, XCircle, 
  Search, Filter, MapPin, Mail, Calendar
} from 'lucide-react';
import { agencyService } from '../../services';
import Pagination, { usePaged } from '../common/Pagination';
import './AgentBenefits.css';

const AgentBenefits = () => {
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    totalAgents: 0,
    agentsWithCommissions: 0,
    agentsWithoutCommissions: 0
  });

  useEffect(() => {
    loadBenefitsData();
  }, []);

  useEffect(() => {
    filterAgents();
  }, [agents, searchTerm, statusFilter]);

  const loadBenefitsData = async () => {
    try {
      setLoading(true);
      const response = await agencyService.getBenefitsData();
      
      if (response.success) {
        setAgents(response.data.agents);
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to load benefits data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAgents = () => {
    let filtered = [...agents];

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(agent => 
        agent.name?.toLowerCase().includes(search) ||
        agent.email?.toLowerCase().includes(search) ||
        agent.address?.toLowerCase().includes(search) ||
        agent.country?.toLowerCase().includes(search)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'with_commissions') {
        filtered = filtered.filter(agent => agent.has_submitted_commission === 1);
      } else if (statusFilter === 'without_commissions') {
        filtered = filtered.filter(agent => agent.has_submitted_commission === 0);
      }
    }

    setFilteredAgents(filtered);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusIcon = (hasCommission) => {
    return hasCommission === 1 ? (
      <CheckCircle className="w-6 h-6 text-green-500" />
    ) : (
      <XCircle className="w-6 h-6 text-red-500" />
    );
  };

  const getStatusText = (hasCommission) => {
    return hasCommission === 1 ? 'Has Submitted' : 'Never Submitted';
  };

  const getStatusClass = (hasCommission) => {
    return hasCommission === 1 ? 'status-success' : 'status-error';
  };

  const paged = usePaged(filteredAgents, `${searchTerm}|${statusFilter}`);

  if (loading) {
    return (
      <div className="benefits-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading benefits data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="benefits-container">
      {/* Header */}
      <div className="benefits-header">
        <div className="header-content">
          <Award className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="header-title">Agent Benefits</h1>
            <p className="header-subtitle">Commission submission status for all agents (Last 12 Months)</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <Users className="w-6 h-6 text-blue-600" />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.totalAgents}</div>
            <div className="stat-label">Total Agents</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.agentsWithCommissions}</div>
            <div className="stat-label">With Commissions (12mo)</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <XCircle className="w-6 h-6 text-red-600" />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.agentsWithoutCommissions}</div>
            <div className="stat-label">Without Commissions (12mo)</div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="controls-section">
        <div className="search-container">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, address, or country..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-container">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Agents</option>
            <option value="with_commissions">With Commissions (12mo)</option>
            <option value="without_commissions">Without Commissions (12mo)</option>
          </select>
        </div>
      </div>

      {/* Agents Grid */}
      <div className="agents-section">
        {filteredAgents.length === 0 ? (
          <div className="empty-state">
            <Award className="w-16 h-16 text-gray-300" />
            <h3>No agents found</h3>
            <p>Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <div className="agents-grid">
            {paged.pageItems.map((agent) => (
              <div key={agent.id} className={`agent-card ${getStatusClass(agent.has_submitted_commission)}`}>
                <div className="agent-card-header">
                  <div className="agent-info">
                    <h3 className="agent-name">{agent.name}</h3>
                    <div className="agent-email">
                      <Mail className="w-4 h-4" />
                      <span>{agent.email}</span>
                    </div>
                  </div>
                  <div className="status-indicator">
                    {getStatusIcon(agent.has_submitted_commission)}
                  </div>
                </div>

                <div className="agent-details">
                  {(agent.address || agent.country) && (
                    <div className="location-info">
                      <MapPin className="w-4 h-4" />
                      <span>
                        {agent.address && agent.country 
                          ? `${agent.address}, ${agent.country}`
                          : agent.address || agent.country || 'Location not specified'
                        }
                      </span>
                    </div>
                  )}

                  <div className="member-since">
                    <Calendar className="w-4 h-4" />
                    <span>Member since: {formatDate(agent.created_at)}</span>
                  </div>
                </div>

                <div className="commission-status">
                  <span className={`status-badge ${getStatusClass(agent.has_submitted_commission)}`}>
                    {getStatusText(agent.has_submitted_commission)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        <Pagination {...paged} />
      </div>
    </div>
  );
};

export default AgentBenefits;
